import { useCallback, useEffect, useRef, useState } from 'react';

// How long `copiedText` stays set after a successful copy.
const COPIED_RESET_MS = 1500;

interface UseCopyToClipboardResult {
  // Resolves true once the text is on the clipboard. Never throws: an unavailable clipboard
  // (insecure context, denied permission) resolves false.
  copyToClipboard: (text: string) => Promise<boolean>;
  // The last text copied, for COPIED_RESET_MS afterwards.
  copiedText?: string;
}

export const useCopyToClipboard = (): UseCopyToClipboardResult => {
  const [copiedText, setCopiedText] = useState<string>();
  const resetTimer = useRef<ReturnType<typeof setTimeout>>(undefined);
  const unmounted = useRef(false);

  useEffect(() => {
    unmounted.current = false;
    return () => {
      unmounted.current = true;
      clearTimeout(resetTimer.current);
    };
  }, []);

  const copyToClipboard = useCallback(async (text: string) => {
    try {
      if (!navigator.clipboard) return false;
      await navigator.clipboard.writeText(text);
    } catch {
      return false;
    }
    // The clipboard write is async: don't set state or start a timer if the caller unmounted meanwhile.
    if (unmounted.current) return true;
    setCopiedText(text);
    clearTimeout(resetTimer.current);
    resetTimer.current = setTimeout(() => setCopiedText(undefined), COPIED_RESET_MS);
    return true;
  }, []);

  return { copyToClipboard, copiedText };
};
